import * as React from "react"
import { createPortal } from "react-dom"
import { CheckIcon, PlusSquareIcon, ShareIcon, XIcon } from "lucide-react"

import { cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { AppIconTile } from "@/shared/pwa/app-icon-tile"
import { IosShareButtonPlacement, iosShareButtonPlacement } from "@/shared/pwa/pwa-install"

/** One gesture of the guide: the button to look for, and what to tap. */
function GuideStep({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className='flex min-h-11 items-center gap-3'>
      <span className='flex size-9 shrink-0 items-center justify-center rounded-[0.625rem] bg-muted text-foreground'>{icon}</span>
      <span className='min-w-0 flex-1 text-[15px] leading-snug font-semibold break-words'>{label}</span>
    </li>
  )
}

/**
 * How to install on an iPhone, where websites cannot install themselves: a bubble over the dimmed page pointing
 * at the browser's own Share button (Safari's bottom bar, or the top bar in Chrome and the others), with the
 * three taps. Tapping outside or the cross closes it.
 */
export function IosInstallGuide({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("common.pwaInstall")
  const placement = iosShareButtonPlacement()
  const atTop = placement === IosShareButtonPlacement.Top

  React.useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onOpenChange(false)
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onOpenChange, open])

  if (!open) return null
  return createPortal(
    <div className='fixed inset-0 z-50' role='dialog' aria-modal='true' aria-labelledby='ios-install-guide-title'>
      <button
        type='button'
        tabIndex={-1}
        aria-label={t("close")}
        data-slot='pwa-guide-overlay'
        className='absolute inset-0 cursor-default bg-black/45'
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          "absolute inset-x-4 rounded-[1.375rem] bg-card p-[18px] text-foreground shadow-2xl",
          atTop
            ? "top-[max(0.75rem,env(safe-area-inset-top))] sm:left-auto sm:w-[22rem]"
            : "bottom-[max(1.25rem,env(safe-area-inset-bottom))] sm:mx-auto sm:max-w-[24rem]",
        )}
      >
        <div className='flex items-center gap-3'>
          <AppIconTile className='size-10 rounded-[0.625rem]' logoClassName='size-6' />
          <h2 id='ios-install-guide-title' className='min-w-0 flex-1 truncate text-[17px] font-extrabold'>
            {t("guideTitle")}
          </h2>
          <button
            type='button'
            onClick={() => onOpenChange(false)}
            aria-label={t("close")}
            className='flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'
          >
            <XIcon className='size-4' aria-hidden />
          </button>
        </div>
        <ol className='mt-3 flex flex-col gap-1.5'>
          <GuideStep
            icon={<ShareIcon className='size-[18px] text-[#007aff]' aria-hidden />}
            label={atTop ? t("stepShareTop") : t("stepShareBottom")}
          />
          <GuideStep icon={<PlusSquareIcon className='size-[18px]' aria-hidden />} label={t("stepAddToHome")} />
          <GuideStep icon={<CheckIcon className='size-[18px] text-primary' aria-hidden />} label={t("stepConfirm")} />
        </ol>
        {/* The tail of the bubble, towards the Share button. */}
        <span
          aria-hidden
          className={cn(
            "absolute size-[18px] rotate-45 bg-card",
            atTop ? "-top-2 right-10" : "-bottom-2 left-1/2 -translate-x-1/2",
          )}
        />
      </div>
    </div>,
    document.body,
  )
}
